import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine, faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDatabase, faChartBar, faRandom, faWheatAwn, faUsers,
    faShieldHalved, faDesktop, faBrain
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/FoodOptimiserMissionControl-Process.png";
import vis from "../../assets/images/use-cases/FoodOptimiserMissionControl-Dashboard.gif";

const FoodOptimiserMissionControl = () => {
    return (
        <>
            <SEO
                title="Food Distribution Optimization: AI for Humanitarian Aid | Nebulyx AI"
                description="Optimize food distribution to maximize beneficiary impact. AI-powered vulnerability mapping, resource allocation, and real-time delivery tracking for humanitarian operations."
                keywords="food distribution, humanitarian AI, vulnerability mapping, resource allocation, food security, beneficiary matching"
                canonicalUrl="https://nebulyx.ai/use-cases/food-optimisation-mission-control"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div>
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faWheatAwn}/>
                                            &nbsp;
                                            Humanitarian
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we get the right food to the right families?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Optimize food distribution across regions to maximize beneficiary impact using AI-driven vulnerability mapping, resource allocation, and real-time delivery tracking.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faUsers} className="stat-icon"/>
                                <span className="stat-value">2.8M</span>
                                <span className="stat-label">Families Reached</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faShieldHalved} className="stat-icon"/>
                                <span className="stat-value">23%</span>
                                <span className="stat-label">More Efficient Allocation</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">5 Regions</span>
                                <span className="stat-label">Coordinated in Real-Time</span>
                            </Col>
                        </Row>
                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper problem me-1"}>
                                                <FontAwesomeIcon icon={faExclamationTriangle}/>
                                            </span>
                                            The Problem
                                        </h3>
                                        <ul className="summary-list">
                                            <li>Food reaching wrong beneficiaries</li>
                                            <li>No real-time visibility across regions</li>
                                            <li>Manual vulnerability assessments outdated</li>
                                            <li>Resource allocation based on estimates not data</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper solution me-1"}>
                                                <FontAwesomeIcon icon={faLightbulb}/>
                                            </span>
                                            The Solution
                                        </h3>
                                        <ul className="summary-list">
                                            <li>AI-powered vulnerability mapping</li>
                                            <li>K-Means beneficiary clustering</li>
                                            <li>Monte Carlo allocation simulation</li>
                                            <li>Real-time beneficiary-food matching</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}>
                                            <span className={"card-icon-wrapper benefits me-1"}>
                                                <FontAwesomeIcon icon={faRocket}/>
                                            </span>
                                            The Benefits
                                        </h3>
                                        <ul className="summary-list">
                                            <li>23% more efficient resource allocation</li>
                                            <li>Real-time delivery tracking</li>
                                            <li>Data-driven prioritization</li>
                                            <li>Measurable beneficiary impact</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests household surveys, warehouse inventories, climate forecasts, economic indicators, and delivery data to map beneficiary needs and simulate allocation changes.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Household surveys, warehouse stocks, weather data, health records, economic indicators</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Data Object Graph maps beneficiary vulnerability, supply chains, and distribution gaps</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Run Scenarios</strong>
                                    <span>What-if engine simulates allocation changes, routing options, priority shifts</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise Output</strong>
                                    <span>Recommended allocations, delivery routes, and predicted beneficiary impact per scenario</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* Digital Twin Section */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>

                        {/* Dashboard */}
                        <Row className="mb-4">
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faBrain}/>
                                            </div>
                                            <h3>Distribution Intelligence Engine</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects household surveys, warehouse inventories, climate forecasts, and economic indicators to build a complete picture of food insecurity. Identifies vulnerable populations, optimizes resource allocation, and predicts intervention impact before deployment.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Mission Control Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Provides real-time visibility across all distribution regions, tracks families reached and food delivered, and tells the human story behind the numbers. Enables teams to simulate allocation scenarios and measure beneficiary impact before committing resources.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Sources</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Household Survey Data</li>
                                            <li>Weather & Climate</li>
                                            <li>Warehouse Inventory</li>
                                            <li>Geospatial / Location</li>
                                            <li>Health & Nutrition Records</li>
                                            <li>Economic Indicators</li>
                                            <li>Transport Management</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Food Consumption Score</li>
                                            <li>Vulnerability Mapping (K-Means)</li>
                                            <li>Resource Allocation Optimization</li>
                                            <li>Impact Assessment (ML Regression)</li>
                                            <li>Food Security Classification</li>
                                            <li>Beneficiary Feedback Analysis</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Regional priority rebalancing</li>
                                            <li>Warehouse pre-positioning</li>
                                            <li>Transport route optimization</li>
                                            <li>Emergency surge response</li>
                                            <li>Seasonal demand forecasting</li>
                                            <li>Budget constraint scenarios</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling beneficiary vulnerability as a <strong>Data Object Graph</strong>, we can make discoveries like
                                        <strong> reallocating just 12% of resources</strong> based on real-time Food Consumption Scores rather than
                                        historical estimates <strong>reaches 340,000 additional families</strong> in critical need.
                                        Graph traversal reveals hidden connections between climate events, economic shocks, and food insecurity.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Impact Multiplier</span>
                                        <span className="discovery-value">+340K Families</span>
                                        <span className="discovery-note">With same resources</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Need to Optimize Humanitarian Distribution?</h2>
                            <p>
                                If your organization struggles to get the right resources to the right people,
                                let's build your AI-powered distribution system together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default FoodOptimiserMissionControl;